import { Box, Transition } from '@mantine/core';
import { forwardRef } from 'react';
import { useInView, useReducedMotion } from '../hooks';

export type RevealAnimation = 'fade' | 'slide-up' | 'slide-down' | 'slide-left' | 'slide-right' | 'scale' | 'rotate';

export interface ScrollRevealProps {
  children: React.ReactNode;
  animation?: RevealAnimation;
  duration?: number;
  delay?: number;
  threshold?: number;
  triggerOnce?: boolean;
  rootMargin?: string;
  distance?: number;
  timingFunction?: string;
}

const ScrollReveal = forwardRef<HTMLDivElement, ScrollRevealProps>(
  (
    {
      children,
      animation = 'fade',
      duration = 500,
      delay = 0,
      threshold = 0.1,
      triggerOnce = true,
      rootMargin = '0px',
      distance = 30,
      timingFunction = 'ease-out',
    },
    ref
  ) => {
    const reducedMotion = useReducedMotion();
    const { ref: inViewRef, inView } = useInView({
      threshold,
      triggerOnce,
      rootMargin,
    });

    const actualDuration = reducedMotion ? 0 : duration;

    const getTransition = () => {
      switch (animation) {
        case 'slide-up':
          return {
            in: { opacity: 1, transform: 'translateY(0)' },
            out: { opacity: 0, transform: `translateY(${distance}px)` },
            transitionProperty: 'opacity, transform',
          };
        case 'slide-down':
          return {
            in: { opacity: 1, transform: 'translateY(0)' },
            out: { opacity: 0, transform: `translateY(-${distance}px)` },
            transitionProperty: 'opacity, transform',
          };
        case 'slide-left':
          return {
            in: { opacity: 1, transform: 'translateX(0)' },
            out: { opacity: 0, transform: `translateX(${distance}px)` },
            transitionProperty: 'opacity, transform',
          };
        case 'slide-right':
          return {
            in: { opacity: 1, transform: 'translateX(0)' },
            out: { opacity: 0, transform: `translateX(-${distance}px)` },
            transitionProperty: 'opacity, transform',
          };
        case 'scale':
          return {
            in: { opacity: 1, transform: 'scale(1)' },
            out: { opacity: 0, transform: 'scale(0.9)' },
            transitionProperty: 'opacity, transform',
          };
        case 'rotate':
          return {
            in: { opacity: 1, transform: 'rotate(0deg) scale(1)' },
            out: { opacity: 0, transform: 'rotate(-10deg) scale(0.9)' },
            transitionProperty: 'opacity, transform',
          };
        case 'fade':
        default:
          return 'fade';
      }
    };

    return (
      <Box ref={inViewRef}>
        <Transition
          mounted={inView}
          transition={getTransition()}
          duration={actualDuration}
          timingFunction={timingFunction}
        >
          {(styles) => (
            <Box
              ref={ref}
              style={{
                ...styles,
                transitionDelay: `${delay}ms`,
              }}
            >
              {children}
            </Box>
          )}
        </Transition>
      </Box>
    );
  }
);

ScrollReveal.displayName = 'ScrollReveal';

export default ScrollReveal;
